import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { 
  Users, X, RefreshCw, ChevronLeft, ChevronRight,
  Shield, CheckCircle2, Building2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ isOpen, onClose }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');

  const pageSize = 15;

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients?page=1&page_size=100`);
      if (!response.ok) throw new Error('Failed to fetch clients');
      
      const data = await response.json();
      setClients(data.clients.map(c => ({
        client_id: c.client_id,
        company_name: c.company_name || `Client ${c.client_id.slice(-6)}`,
        created_at: c.created_at || new Date().toISOString()
      })));
      
    } catch (err) {
      setError('Failed to load clients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen, fetchClients]);

  // Sort by most recent
  const sortedClients = [...clients].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );

  // Paginate
  const totalItems = sortedClients.length;
  const calculatedTotalPages = Math.ceil(totalItems / pageSize);
  const paginatedClients = sortedClients.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setTotalPages(calculatedTotalPages || 1);
  }, [calculatedTotalPages]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-600 to-cyan-600">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-white" />
            <h2 className="text-xl font-semibold text-white">Client Bookings</h2>
            <span className="px-3 py-1 text-xs font-bold text-emerald-900 bg-emerald-100 rounded-full">
              {totalItems} clients
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stats Banner */}
        <div className="px-6 py-3 bg-gradient-to-r from-emerald-50 to-cyan-50 border-b border-emerald-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">
                Clients who completed the booking process
              </span>
            </div>
            <Button onClick={fetchClients} variant="ghost" size="sm" className="text-emerald-700 hover:text-emerald-800">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Clients List */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : paginatedClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-500">
              <Users className="w-12 h-12 mb-2 text-gray-300" />
              <p>No clients yet</p>
              <p className="text-sm text-gray-400 mt-1">Clients will appear here after completing a booking</p>
            </div>
          ) : (
            <div className="space-y-2">
              {paginatedClients.map((client) => (
                <div
                  key={client.client_id}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:border-emerald-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-purple-500 to-indigo-600">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {client.company_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        ID: {client.client_id}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Booking Confirmed
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalItems)} of {totalItems}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="flex items-center px-3 text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-3 bg-emerald-50 border-t border-emerald-100 text-center">
          <p className="text-xs text-emerald-700">
            🔒 Privacy-first: Only showing client IDs and company names
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
